import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  IoSearchOutline,
  IoCloseOutline,
  IoPeopleOutline,
  IoPersonAddSharp
} from 'react-icons/io5';
import { useAuth } from '../hooks/useAuth';
import { useChat } from '../contexts/ChatContext';
import { friendshipApi } from '../services/friendshipApi';
import UserRelationshipCard, { UserRelationshipCardSkeleton } from '../components/UserRelationshipCard';
import './Friends.css';

const Friends = () => {
  const { t } = useTranslation();
  const { user, refreshPendingRequests } = useAuth();
  const { createConversation, setActiveConversationId } = useChat();
  const navigate = useNavigate();

  const [friendships, setFriendships] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('friends');

  useEffect(() => {
    loadFriendships();
    loadSuggestions();
    refreshPendingRequests();
  }, []);

  const loadFriendships = async () => {
    try {
      const response = await friendshipApi.getFriendships();
      if (response.success) {
        setFriendships(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load friendships:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      const response = await friendshipApi.getSuggestions(10);
      if (response.success) {
        setSuggestions(response.data.suggestions || []);
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleAccept = async (friendUser) => {
    try {
      await friendshipApi.acceptFriendRequest(friendUser.friendshipId);
      loadFriendships();
      refreshPendingRequests();
    } catch (error) {
      console.error('Failed to accept request:', error);
    }
  };

  const handleReject = async (friendUser) => {
    try {
      await friendshipApi.deleteFriendship(friendUser.friendshipId);
      loadFriendships();
      refreshPendingRequests();
    } catch (error) {
      console.error('Failed to reject request:', error);
    }
  };

  const handleCancelRequest = async (friendUser) => {
    try {
      await friendshipApi.deleteFriendship(friendUser.friendshipId);
      loadFriendships();
    } catch (error) {
      console.error('Failed to cancel request:', error);
    }
  };

  const handleAddFriend = async (friendUser) => {
    try {
      await friendshipApi.sendFriendRequest(friendUser.id);
      setSuggestions(prev => prev.filter(s => s.id !== friendUser.id));
      loadFriendships();
    } catch (error) {
      console.error('Failed to send friend request:', error);
    }
  };

  const handleUnfriend = async (friendUser) => {
    try {
      await friendshipApi.deleteFriendship(friendUser.friendshipId);
      loadFriendships();
    } catch (error) {
      console.error('Failed to unfriend:', error);
    }
  };

  const handleMessage = async (friendUser) => {
    try {
      const conv = await createConversation([friendUser.id], false);
      setActiveConversationId(conv.id);
      navigate('/chat');
    } catch (error) {
      console.error('Failed to start conversation:', error);
    }
  };

  if (loading) {
    return (
      <div className="friends-container fade-in">
        <div className="friends-header">
          <h1>{t('friends.friends')}</h1>
        </div>
        <div className="friends-list-loading">
          {[1, 2, 3, 4, 5].map(i => (
            <UserRelationshipCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  const pendingRequests = friendships.filter(
    (f) => f.status === 'pending' && f.addressee_id === user.id
  );

  const sentRequests = friendships.filter(
    (f) => f.status === 'pending' && f.requester_id === user.id
  );

  const acceptedFriendships = friendships.filter((f) => f.status === 'accepted');

  // Helper to get friend object from friendship
  const getFriendFromFriendship = (friendship) => {
    return friendship.requester_id === user.id
      ? { ...friendship.addressee, friendshipId: friendship.id }
      : { ...friendship.requester, friendshipId: friendship.id };
  };

  // Build unified list based on active tab
  const getUnifiedList = () => {
    let list = [];

    if (activeTab === 'friends') {
      list = acceptedFriendships.map(f => ({
        ...getFriendFromFriendship(f),
        relationship: 'friends'
      }));
    } else if (activeTab === 'requests') {
      // Pending received requests
      list = pendingRequests.map(f => ({
        ...f.requester,
        friendshipId: f.id,
        relationship: 'pending_received'
      }));
      // Add sent requests
      list = [...list, ...sentRequests.map(f => ({
        ...f.addressee,
        friendshipId: f.id,
        relationship: 'pending_sent'
      }))];
    } else if (activeTab === 'suggestions') {
      list = suggestions.map(s => ({
        ...s,
        relationship: 'none'
      }));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = list.filter(item =>
        item.display_name?.toLowerCase().includes(query) ||
        item.username?.toLowerCase().includes(query)
      );
    }

    return list;
  };

  const unifiedList = getUnifiedList();

  // Get empty state message
  const getEmptyState = () => {
    if (searchQuery.trim()) {
      return {
        icon: <IoSearchOutline />,
        title: t('friends.noResults') || 'No results found',
        desc: `No users matching "${searchQuery}"`
      };
    }

    if (activeTab === 'friends') {
      return {
        icon: <IoPeopleOutline />,
        title: t('profile.noFriendsYet') || 'No connections yet',
        desc: t('friends.noFriendsFound') || 'Start adding friends to grow your network'
      };
    }

    if (activeTab === 'requests') {
      return {
        icon: <IoPersonAddSharp />,
        title: t('friends.noFriendRequests') || 'No friend requests',
        desc: t('friends.noRequestsDesc') || 'When someone sends you a friend request, it will appear here'
      };
    }

    return {
      icon: <IoPeopleOutline />,
      title: t('friends.noSuggestionsTitle') || 'No suggestions right now',
      desc: t('friends.noSuggestionsDesc') || 'Try searching for friends or check back later.'
    };
  };

  const emptyState = getEmptyState();

  return (
    <div className="friends-container fade-in">
      {/* Header */}
      <div className="friends-header">
        <h1>{t('friends.friends')}</h1>
      </div>

      {/* Search */}
      <div className="friends-search">
        <IoSearchOutline className="search-icon" />
        <input
          type="text"
          placeholder={t('friends.searchFriends') || 'Search friends...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="clear-search" onClick={() => setSearchQuery('')}>
            <IoCloseOutline />
          </button>
        )}
      </div>

      {/* Tabs Navigation */}
      <div className="friends-tabs">
        <button
          className={`tab-btn ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          <IoPeopleOutline />
          {t('friends.friends')}
          {acceptedFriendships.length > 0 && <span className="tab-count">({acceptedFriendships.length})</span>}
        </button>
        <button
          className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <IoPersonAddSharp />
          {t('friends.requests') || 'Requests'}
          {pendingRequests.length > 0 && <span className="tab-count">{pendingRequests.length}</span>}
        </button>
        <button
          className={`tab-btn ${activeTab === 'suggestions' ? 'active' : ''}`}
          onClick={() => setActiveTab('suggestions')}
        >
          <IoPeopleOutline />
          {t('friends.suggestions')}
        </button>
      </div>

      {/* Loading State for Suggestions */}
      {activeTab === 'suggestions' && suggestionsLoading && (
        <div className="friends-list-loading">
          {[1, 2, 3, 4, 5].map(i => (
            <UserRelationshipCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Unified List */}
      {!suggestionsLoading && (
        <>
          {unifiedList.length > 0 ? (
            <div className="friendship-list unified">
              {unifiedList.map((item) => (
                <UserRelationshipCard
                  key={item.id}
                  user={item}
                  relationship={item.relationship}
                  mutualFriends={item.mutual_friends_count || 0}
                  onAddFriend={handleAddFriend}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  onCancelRequest={handleCancelRequest}
                  onUnfriend={handleUnfriend}
                  onMessage={handleMessage}
                />
              ))}
            </div>
          ) : (
            <div className="friends-empty-state">
              <div className="empty-icon">
                {emptyState.icon}
              </div>
              <p className="empty-title">{emptyState.title}</p>
              <p className="empty-desc">{emptyState.desc}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Friends;
